import { blobToFile, stringToEventCode } from '@vindr/utils/converter';
import { uuidv4 } from '@vindr/utils/helpers/common.helper';
import {
  Dispatch,
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useImmerReducer } from 'use-immer';
import { useCaptureManagerState, useCaptureManagerUpdater } from '../../CaptureManager/context';
import getSupportedMimeTypes, { getExtensionMimeTypes } from '../helper/getSupportedMineTypes';
import { cacheVideoData, closeDB, openDatabase, restoreCachedVideo } from '../helper/storeIndexDB';
import { appendImgPreview, getRecordingBlob } from '../helper/webcam-helper';
import captureReducer from './reducer';
import { initialState } from './state';
import {
  CaptureAction,
  CaptureContextProps,
  CaptureHandle,
  CaptureProviderProps,
  CaptureState,
} from './type';

const CaptureStateContext = createContext<CaptureContextProps>({} as CaptureContextProps);
const CaptureUpdaterContext = createContext<Dispatch<CaptureAction>>(null);

// polyfillCamera();

const CaptureProvider = forwardRef<CaptureHandle, CaptureProviderProps>(
  ({ children, setRecording }, ref) => {
    const [state, dispatch] = useImmerReducer(captureReducer, initialState);
    const { importFiles, handleUploadFiles, setShowCamera } = useCaptureManagerState();
    const dispatchCaptureManager = useCaptureManagerUpdater();
    const [localVideo, snapshot, captureWrapper]: [
      CaptureState['localVideo'],
      CaptureState['snapshot'],
      CaptureState['captureWrapper']
    ] = [useRef(null), useRef(null), useRef(null)];
    const mimeType = getSupportedMimeTypes('video')[1];

    const { mediaRecorder, isGranted, enableMic, selectedVideo, selectedResolution, shortcut } =
      state;

    const importImage = useCallback(
      async ({
        url,
        imageType = 'image/jpeg',
        originName,
        id,
      }: {
        url: string;
        imageType?: string;
        originName?: string;
        id?: string;
      }) => {
        const stateId = id || `backup-${uuidv4()}`;
        const name = `${stateId}.${getExtensionMimeTypes(imageType)}`;
        const file = await blobToFile(url, name);

        importFiles({
          url,
          id: stateId,
          tag: 'img',
          name: originName || name,
          type: imageType,
          file,
        });

        return { name };
      },
      [importFiles]
    );

    const handleSnapshot = useCallback(async () => {
      if (!isGranted) return;

      const canvas = snapshot.current;
      const ctx = canvas.getContext('2d');

      canvas.width = localVideo.current.videoWidth;
      canvas.height = localVideo.current.videoHeight;

      ctx.drawImage(localVideo.current, 0, 0, canvas.width, canvas.height);

      const imageType = 'image/jpeg';

      const url = canvas.toDataURL(imageType);

      const id = uuidv4();
      const backupId = `backup-${id}`;
      const { name } = await importImage({ url, imageType, id });

      cacheVideoData(backupId, {
        name,
        url,
        id: backupId,
      });

      //? append preview capture on fullscreen
      if (state.isFullScreen) {
        appendImgPreview(url);
      } else {
        captureWrapper.current.classList.add('shotting');

        captureWrapper.current.addEventListener('animationend', () => {
          captureWrapper.current.classList.remove('shotting');
        });
      }
    }, [captureWrapper, importImage, isGranted, localVideo, snapshot, state.isFullScreen]);

    const startTimer = useCallback(() => {
      const intervalId = setInterval(() => {
        dispatch({ type: 'TICK' });
      }, 1000);

      dispatch({ type: 'START_TIMER', payload: intervalId });
      setRecording?.((draft) => {
        draft.recording = true;
      });
    }, [dispatch]);

    const stopTimer = useCallback(() => {
      clearInterval(state.intervalId);
      dispatch({ type: 'STOP_TIMER' });

      setRecording?.((draft) => {
        draft.recording = false;
      });
    }, [dispatch, state.intervalId]);

    const pauseTimer = useCallback(() => {
      dispatch({ type: 'PAUSE_TIMER' });
    }, [dispatch]);

    const importRecord = useCallback(
      async (
        chunks: BlobPart[],
        info: {
          name: string;
          id: string;
        }
      ) => {
        //? Fix duration, because duration of webm file is 0. And fix webm file is not supported in Safari. And Fix webm file larger than 2GB
        //? Only need on webm file, other file is skip
        const blob = new Blob(chunks, { type: mimeType });
        const fixBlob =
          getExtensionMimeTypes(mimeType) === 'webm' ? await getRecordingBlob(blob) : blob;

        const url = URL.createObjectURL(fixBlob);

        importFiles({
          url,
          id: info?.id || `backup-${uuidv4()}`,
          tag: 'video',
          name: info?.name,
          type: mimeType,
          file: fixBlob,
        });
      },
      []
    );

    const stopRecord: CaptureHandle['stopRecord'] = useCallback(
      (enableCheckConvert?: boolean) => {
        if (!isGranted) return;
        stopTimer();

        dispatch({
          type: 'STOP_MEDIA_RECORDER',
          payload: ['recording', 'paused'].includes(mediaRecorder?.state),
        });

        if (enableCheckConvert) {
          dispatchCaptureManager({
            type: 'SET_CONVERTING',
            payload: { enable: true, status: null },
          });
        }
      },
      [dispatch, dispatchCaptureManager, isGranted, mediaRecorder?.state, stopTimer]
    );

    const startRecord = useCallback(() => {
      if (!isGranted) return;

      navigator.mediaDevices
        .getUserMedia({
          video: {
            deviceId: selectedVideo.deviceId,
            groupId: selectedVideo.groupId,
            // width: selectedResolution?.width,
            // height: selectedResolution?.height,
            // width: { ideal: 2048 },
            // height: { ideal: 1080 },
          },
          audio: enableMic,
        })
        .then(async (stream) => {
          if (['recording', 'paused'].includes(mediaRecorder?.state)) {
            stopRecord();

            return;
          }
          startTimer();
          const recorder = new MediaRecorder(stream, { mimeType });
          const chunks = [];

          dispatch({ type: 'SET_RECORD_STREAM', payload: stream });
          dispatch({ type: 'SET_MEDIA_RECORDER', payload: recorder });

          const fileName = `${uuidv4()}.${getExtensionMimeTypes(mimeType)}`;
          const id = `backup-${uuidv4()}`;

          recorder.ondataavailable = (e) => {
            const data = e.data;
            if (!data.size) return;

            chunks.push(data);

            cacheVideoData(id, {
              url: data,
              name: fileName,
              id,
            });
          };

          recorder.onstop = async () =>
            importRecord(chunks, {
              name: fileName,
              id,
            });
          recorder.onpause = () => pauseTimer();
          recorder.onresume = () => startTimer();

          // Slice 1s / 1 blob record
          recorder.start(1000);
        });
    }, [
      isGranted,
      selectedVideo,
      enableMic,
      mediaRecorder?.state,
      startTimer,
      mimeType,
      dispatch,
      stopRecord,
      importRecord,
      pauseTimer,
    ]);

    const pauseRecord = useCallback(() => {
      if (!isGranted || !mediaRecorder) return;

      if (mediaRecorder.state === 'paused') {
        mediaRecorder.resume();
      } else if (mediaRecorder.state === 'recording') {
        mediaRecorder.pause();
      }
    }, [isGranted, mediaRecorder]);

    const resumeRecord = useCallback(() => {
      if (!isGranted || !mediaRecorder) return;

      if (mediaRecorder.state === 'paused') {
        mediaRecorder.resume();
      } else if (mediaRecorder.state === 'recording') {
        mediaRecorder.pause();
      }
    }, [isGranted, mediaRecorder]);

    const handleFullScreen = useCallback(() => {
      if (!isGranted) return;
      // fullScreen(localVideo.current); //? Righter for using captureWrapper.current, but need absolute dropDown setting
      dispatch({
        type: 'SET_FULLSCREEN',
        payload: captureWrapper.current.classList.toggle('fullscreen'),
      });
    }, [captureWrapper, dispatch, isGranted]);

    const memoizedValue = useMemo(
      () => ({
        ...state,
        localVideo,
        snapshot,
        captureWrapper,
        setRecording,
        handleSnapshot,
        startRecord,
        stopRecord,
        handleFullScreen,
        pauseRecord,
        resumeRecord,
      }),
      [
        state,
        localVideo,
        snapshot,
        captureWrapper,
        setRecording,
        handleSnapshot,
        startRecord,
        stopRecord,
        handleFullScreen,
        pauseRecord,
        resumeRecord,
      ]
    );

    useLayoutEffect(() => {
      openDatabase()
        .then(async () => {
          const caches = await restoreCachedVideo();

          if (!caches || !Array.isArray(caches)) return;

          caches.forEach((cache) => {
            if (
              Array.isArray(cache?.url) &&
              (cache?.url?.[0] instanceof Blob || cache?.url?.[0] instanceof ArrayBuffer)
            ) {
              importRecord(cache?.url as BlobPart[], { name: cache?.name, id: cache?.id });
            } else {
              const url = cache?.url as string;
              importImage({
                url,
                originName: cache?.name,
                id: cache?.id,
              });
            }
          });
        })
        .catch(console.error);

      return () => {
        closeDB();
      };
    }, []);

    const actionShortcut = useMemo(
      () => ({
        [shortcut.snapshot]: {
          action: handleSnapshot,
          eventCode: stringToEventCode(shortcut.snapshot),
        },
        [shortcut.record]: {
          action: startRecord,
          eventCode: stringToEventCode(shortcut.record),
        },
        [shortcut.fullscreen]: {
          action: handleFullScreen,
          eventCode: stringToEventCode(shortcut.fullscreen),
        },
        [shortcut.pause]: {
          action: pauseRecord,
          eventCode: stringToEventCode(shortcut.pause),
        },
      }),
      [
        handleFullScreen,
        handleSnapshot,
        pauseRecord,
        shortcut.fullscreen,
        shortcut.pause,
        shortcut.record,
        shortcut.snapshot,
        startRecord,
      ]
    );

    const handleKeyDown: CaptureHandle['handleKeyDown'] = useCallback(
      async (e) => {
        const { ctrlKey, shiftKey, altKey, key } = e;

        if (e.key === 'Alt' || e.code === 'AltLeft' || e.code === 'AltRight') {
          e.preventDefault();

          return false;
        }

        const action = Object.values(actionShortcut).find(
          (it) =>
            it.eventCode.altKey === altKey &&
            it.eventCode.key === key &&
            it.eventCode.ctrlKey === ctrlKey &&
            it.eventCode.shiftKey === shiftKey
        );

        // e.preventDefault();
        // disabledEventPropagation(e);

        if (!action) return;

        await action.action();
      },
      [actionShortcut]
    );

    useImperativeHandle(
      ref,
      () => {
        return {
          handleKeyDown,
          handleUploadFiles,
          setShowCamera,
          stopRecord,
        };
      },
      [handleKeyDown, handleUploadFiles, setShowCamera, stopRecord]
    );

    // Shortcut
    useEffect(() => {
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [handleKeyDown]);

    return (
      <CaptureStateContext.Provider value={memoizedValue}>
        <CaptureUpdaterContext.Provider value={dispatch}>{children}</CaptureUpdaterContext.Provider>
      </CaptureStateContext.Provider>
    );
  }
);

CaptureProvider.displayName = 'CaptureProvider';

export default memo(CaptureProvider);

export const useCaptureState = () => {
  const context = useContext(CaptureStateContext);

  if (!context) {
    throw new Error('useCaptureState must be used within a CaptureStateContext');
  }

  return context;
};

export const useCaptureUpdater = () => {
  const context = useContext(CaptureUpdaterContext);

  if (!context) {
    throw new Error('useCaptureUpdater must be used within a CaptureUpdaterContext');
  }

  return context;
};
